import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useBlocker, useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {RecordPage} from '../../shared/components/record/RecordKit';
import {getInvoiceDueDate, saveInvoiceDueDate} from '../../shared/services/company-settings.service';
import {InvoiceDueDateSetting} from '../../shared/dtos/company-settings.dto';
import {SavaquanHttpError} from '../../shared/utils/http-error';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';
import {
  FollowUpsForm, MAX_FOLLOW_UPS, badDays, badMethod, cardMessages, hasProblems, newFollowUpRow, sameFollowUps,
} from '../../shared/components/follow-ups/follow-ups-form';
import {
  AddButton, DayPicker, DayPill, ErrorBox, FollowUpCard, Note, SaveRow, SwitchRow,
} from '../../shared/components/follow-ups/FollowUpKit';
import './InvoiceDueDatesPage.css';

/*
 * SQ-687 / SQ-688: Due dates and follow-ups, opened from the invoices list's
 * "Set due dates and follow-ups". A port of Android's InvoiceDueScreen.kt:
 * the due date, then the follow-up emails after it (the estimate follow-ups
 * page's cards, counted from the due date), all under one Save changes.
 */

const DUE_NOTE =
  'How long a client has to pay once you send an invoice. This is the due date on every invoice you send.';
const FOLLOW_UPS_NOTE_EMPTY =
  'Set reminders that go out on their own once an invoice is past due, so a client who still owes you '
  + 'hears from you again without you having to chase it.';
const FOLLOW_UPS_NOTE_ON =
  "Counted from the invoice's due date. Reminders stop on their own as soon as the invoice is paid in full "
  + "or deleted, and a client who has paid part is told what's left.";
const FOLLOW_UPS_NOTE_OFF =
  'Your invoice follow-ups are turned off. Nothing goes out on any invoice until you switch them back on.';
const FOOTER =
  'Changes only affect invoices sent from now on. Due dates and follow-ups on invoices you have '
  + 'already sent stay as they were. Switching follow-ups off stops those too.';

/** Where most companies land, so a company that never saved one is already on it (the server agrees). */
const DEFAULT_DUE_DAYS = 14;
/** Choices are 1 through 60 days, every day. */
const DUE_CHOICES = Array.from({length: 60}, (_, i) => i + 1);
/** "Saved" is a confirmation, not a status: three seconds, then gone. */
const SAVED_MS = 3000;
/** Which day list is open: the due date's, or a follow-up card's by its index. */
const DUE_PICKER = -1;

/** "7 days after due", "1 day after due", and "0 days" for the unset one. */
const afterDueLabel = (d: number) => (d === 0 ? '0 days' : `${d} ${d === 1 ? 'day' : 'days'} after due`);
/** "14 days after sending", "1 day after sending". */
const dueLabel = (d: number) => `${d} ${d === 1 ? 'day' : 'days'} after sending`;

/** What the page edits and what the server holds: the due date and the follow-ups after it. */
interface DueForm {
  dueDays: number;
  followUps: FollowUpsForm;
}

// Every saved follow-up goes by email: that is all the server sends.
const formOf = (dto: InvoiceDueDateSetting | null | undefined): DueForm => ({
  dueDays: dto?.dueDays ?? DEFAULT_DUE_DAYS,
  followUps: {
    on: dto?.followUpsEnabled !== false,
    rows: (dto?.followUpDays ?? []).map(days => ({days, text: false, email: true})),
  },
});

const settingOf = (form: DueForm): InvoiceDueDateSetting => ({
  dueDays: form.dueDays,
  followUpDays: form.followUps.rows.map(r => r.days),
  followUpsEnabled: form.followUps.on,
});

const sameForm = (a: DueForm, b: DueForm) => a.dueDays === b.dueDays && sameFollowUps(a.followUps, b.followUps);

const InvoiceDueDatesPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  /** The saved setting never arrived; the page offers Try again instead of a form. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<DueForm>(formOf(null));
  const [pending, setPending] = useState<DueForm>(formOf(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Only complain about the follow-ups once somebody has actually tried to save. */
  const [tried, setTried] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // One day list open at a time: the due date's or a card's.
  const [picking, setPicking] = useState<number | null>(null);
  // Cards whose locked Text box was tapped, so they say why for a few
  // seconds; the value is the tap, so another tap starts the time over.
  const [textAsked, setTextAsked] = useState<Record<number, number>>({});

  // The latest pending form, for a save's answer: an edit made while the request was out stays pending.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const savedTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    setError(null);
    getInvoiceDueDate(navigate)
      .then(dto => {
        const form = formOf(dto);
        setSaved(form);
        setPending(form);
      })
      .catch(e => {
        if (isAuthRedirectError(e)) return;
        if (e instanceof SavaquanHttpError && e.statusCode === 403) {
          setForbidden(true);
          return;
        }
        logError('Error loading the invoice due date setting', {}, e as Error);
        setLoadError(e instanceof SavaquanHttpError && e.statusCode > 0
          ? `Couldn't load your settings (HTTP ${e.statusCode}).`
          : "Couldn't load your settings. Check your connection and try again.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const isDirty = !loading && !forbidden && !loadError && !sameForm(pending, saved);
  const followUps = pending.followUps;
  const on = followUps.on;
  const empty = followUps.rows.length === 0;

  // Unsaved changes hold up leaving: an in-app move asks first, a reload or a
  // closed tab gets the browser's own prompt.
  const blocker = useBlocker(({currentLocation, nextLocation}) => isDirty && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const edit = (change: (form: DueForm) => DueForm) => {
    setPending(change);
    setJustSaved(false);
    setError(null);
  };
  const editFollowUps = (change: (form: FollowUpsForm) => FollowUpsForm) =>
    edit(form => ({...form, followUps: change(form.followUps)}));
  const editRow = (index: number, change: (row: FollowUpsForm['rows'][number]) => FollowUpsForm['rows'][number]) =>
    editFollowUps(form => ({...form, rows: form.rows.map((r, k) => (k === index ? change(r) : r))}));

  const addFollowUp = () => {
    if (followUps.rows.length >= MAX_FOLLOW_UPS) return;
    setPicking(null);
    // A new card turns the switch on and quiets the last save's complaints.
    editFollowUps(form => ({on: true, rows: [...form.rows, newFollowUpRow()]}));
    setTried(false);
  };

  const removeFollowUp = (index: number) => {
    setPicking(null);
    setTextAsked(asked => Object.fromEntries(Object.entries(asked)
      .filter(([k]) => Number(k) !== index)
      .map(([k, tap]) => [Number(k) > index ? Number(k) - 1 : Number(k), tap])));
    editFollowUps(form => ({...form, rows: form.rows.filter((_, k) => k !== index)}));
    if (followUps.rows.length <= 1) setTried(false);
  };

  const save = async () => {
    if (saving || loading || forbidden || loadError) return;
    setPicking(null);
    setTried(true);
    if (hasProblems(followUps)) return;
    const sent = pending;
    setSaving(true);
    setError(null);
    try {
      const stored = formOf(await saveInvoiceDueDate(navigate, settingOf(sent)));
      const untouched = sameForm(pendingRef.current, sent);
      setSaved(stored);
      if (untouched) setPending(stored);
      setTried(false);
      setJustSaved(untouched);
      window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setJustSaved(false), SAVED_MS);
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error saving the invoice due date setting', {}, e as Error);
      const code = e instanceof SavaquanHttpError ? e.statusCode : 0;
      setError(code === 403 ? "Your access level can't change invoice due dates and follow-ups."
        : code > 0 ? `Couldn't save your changes (HTTP ${code}). Try again.`
          : "Couldn't save your changes. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const body = () => {
    if (loading) return <div className="idp-loading"><CircularProgress sx={{color: 'var(--lp-green)'}}/></div>;
    if (forbidden) return <Note text="Your access level can't change invoice due dates and follow-ups. Ask an owner or admin."/>;
    if (loadError) return <>
      <ErrorBox text={loadError}/>
      <button type="button" className="idp-retry" onClick={load}>Try again</button>
    </>;
    return <>
      {/* One save for the whole page: the due date and the follow-ups. */}
      <SaveRow dirty={isDirty} justSaved={justSaved} saving={saving} onSave={() => void save()}/>
      {tried && hasProblems(followUps) && <ErrorBox text="Fix the follow-ups marked below, then save again."/>}
      {error && <ErrorBox text={error}/>}

      {/* ---- when the invoice is due ---- */}
      <Note text={DUE_NOTE}/>
      {/* One setting, not a record: the plain settings card. */}
      <div className="fu-card" role="group" aria-label="Invoice due date">
        <div className="fu-card-head">
          <span className="fu-card-label">Due</span>
          <DayPill label={dueLabel(pending.dueDays)} on wrong={false} open={picking === DUE_PICKER}
                   onClick={() => setPicking(p => (p === DUE_PICKER ? null : DUE_PICKER))}/>
        </div>
        {picking === DUE_PICKER && (
          <DayPicker choices={DUE_CHOICES} selected={pending.dueDays} label={dueLabel} onPick={d => {
            edit(form => ({...form, dueDays: d}));
            setPicking(null);
          }}/>
        )}
      </div>

      <div className="idp-rule"/>

      {/* ---- chasing it once it is past due ---- */}
      {!empty && (
        <SwitchRow on={on} title="Invoice follow-ups" onLine="On for every invoice you send" onToggle={() => {
          setPicking(null);
          editFollowUps(form => ({...form, on: !form.on}));
        }}/>
      )}
      <Note text={empty ? FOLLOW_UPS_NOTE_EMPTY : !on ? FOLLOW_UPS_NOTE_OFF : FOLLOW_UPS_NOTE_ON}/>
      {followUps.rows.map((row, i) => (
        <FollowUpCard
          key={i}
          index={i}
          row={row}
          on={on}
          dayWrong={on && tried && badDays(followUps, i)}
          methodWrong={on && tried && badMethod(followUps, i)}
          messages={on && tried ? cardMessages(followUps, i) : []}
          textTap={textAsked[i]}
          onTextNoteDone={tap => setTextAsked(asked => {
            if (asked[i] !== tap) return asked;
            const {[i]: _done, ...rest} = asked;
            return rest;
          })}
          pickerOpen={on && picking === i}
          onTogglePicker={() => setPicking(p => (p === i ? null : i))}
          onPick={d => {
            editRow(i, r => ({...r, days: d}));
            setPicking(null);
          }}
          onToggleEmail={() => editRow(i, r => ({...r, email: !r.email}))}
          onTapText={() => setTextAsked(asked => ({...asked, [i]: Date.now()}))}
          onDelete={() => removeFollowUp(i)}
          dayLabel={afterDueLabel}
        />
      ))}
      {on && (followUps.rows.length < MAX_FOLLOW_UPS
        ? <AddButton text={empty ? 'Add a follow-up' : 'Add another follow-up'} onClick={addFollowUp}/>
        : <Note text="Five follow-ups is the most an invoice can have."/>)}

      <p className="idp-footer">{FOOTER}</p>
    </>;
  };

  return (
    // The record pages' column: 750 px at most, grey beside it, and only the column scrolls.
    <RecordPage name="InvoiceDueDatesPage" className="lp fu idp">
      <div className="idp-head">
        <button type="button" className="idp-back" aria-label="Back to invoices" onClick={() => navigate('/invoices')}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 6L8.5 12L14.5 18" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="idp-title">Due dates and follow-ups</h1>
      </div>
      {body()}

      {/* Portaled like every dialog: inside the page, MUI hid it from screen readers. */}
      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()} aria-labelledby="idp-leave-title">
        <DialogTitle id="idp-leave-title">Leave without saving?</DialogTitle>
        <DialogContent><DialogContentText>Your changes have not been saved.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()}>Keep editing</Button>
          <Button color="error" onClick={() => {
            setPending(saved);
            setTried(false);
            blocker.proceed?.();
          }}>Discard changes</Button>
        </DialogActions>
      </Dialog>
    </RecordPage>
  );
};

export default InvoiceDueDatesPage;
